import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listStoryTranslations,
  approveTranslation,
  rejectTranslation,
  saveTranslationEdit,
} from "@/lib/admin.client";
import type { AdminDict } from "@/lib/admin-i18n";

type Row = {
  id: string;
  language: string;
  status: string;
  title: string | null;
  body: string | null;
  created_at: string;
};

export function TranslationsPanel({
  t,
  canonicalId,
  original,
}: {
  t: AdminDict;
  canonicalId: string;
  original: { title: string | null; body: string | null; language: string };
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, { title: string; body: string; reason: string; saved?: boolean }>>({});

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin", "translations", canonicalId],
    queryFn: () => listStoryTranslations({ data: { canonicalId } }),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "translations", canonicalId] });
    qc.invalidateQueries({ queryKey: ["admin"] });
  };

  const approve = useMutation({
    mutationFn: (id: string) => approveTranslation({ data: { id } }),
    onSuccess: invalidate,
    onError: (e) => setNotice((e as Error).message),
  });

  const reject = useMutation({
    mutationFn: (args: { id: string; reason: string }) =>
      rejectTranslation({ data: args }),
    onSuccess: invalidate,
    onError: (e) => setNotice((e as Error).message),
  });

  const approveAll = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await approveTranslation({ data: { id } });
      }
    },
    onSuccess: invalidate,
    onError: (e) => setNotice((e as Error).message),
  });

  const rows: Row[] = (data ?? []) as Row[];
  const reviewIds = rows.filter((r) => r.status === "in_review").map((r) => r.id);

  // Seed / re-seed drafts whenever server rows change (e.g. after save/approve).
  useEffect(() => {
    setDrafts((prev) => {
      const next: typeof prev = {};
      for (const r of rows) {
        const p = prev[r.id];
        next[r.id] = p
          ? p
          : { title: r.title ?? "", body: r.body ?? "", reason: "" };
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const setDraft = (id: string, patch: Partial<{ title: string; body: string; reason: string; saved: boolean }>) =>
    setDrafts((d) => ({ ...d, [id]: { ...(d[id] ?? { title: "", body: "", reason: "" }), ...patch } }));

  const save = useMutation({
    mutationFn: (args: { id: string; title: string; body: string; reason: string }) =>
      saveTranslationEdit({ data: args }),
    onSuccess: (_r, vars) => {
      setDraft(vars.id, { saved: true, reason: "" });
      invalidate();
    },
    onError: (e) => setNotice((e as Error).message),
  });

  return (
    <section className="rounded-sm border border-border p-4">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="text-[11px] uppercase tracking-wider text-muted-foreground">
          {t.translations_title}
        </h4>
        {reviewIds.length > 1 && (
          <button
            disabled={approveAll.isPending}
            onClick={() => approveAll.mutate(reviewIds)}
            className="rounded-full border px-3 py-1 text-xs font-medium disabled:opacity-50"
            style={{ borderColor: "var(--ink)", color: "var(--ink)" }}
          >
            {t.translations_approve_all} ({reviewIds.length})
          </button>
        )}
      </header>

      {isLoading && <p className="mt-2 text-sm text-muted-foreground">…</p>}
      {error && (
        <p className="mt-2 text-sm" style={{ color: "var(--rust)" }}>
          {(error as Error).message}
        </p>
      )}
      {!isLoading && !error && rows.length === 0 && (
        <p className="mt-2 text-sm italic text-muted-foreground">
          {t.translations_empty}
        </p>
      )}

      {rows.length > 0 && (
        <ul className="mt-3 space-y-3">
          {rows.map((r) => {
            const isReview = r.status === "in_review";
            const isOpen = !!open[r.id];
            return (
              <li
                key={r.id}
                className="rounded-sm border p-3"
                style={{ borderColor: "var(--fog)" }}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="flex items-baseline gap-2">
                    <span
                      className="rounded-sm border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider"
                      style={{ borderColor: "var(--ink)", color: "var(--ink)" }}
                    >
                      {r.language}
                    </span>
                    <span className="text-xs text-muted-foreground">{r.status}</span>
                  </div>
                  {isReview && (
                    <div className="flex gap-2">
                      <button
                        disabled={approve.isPending || approveAll.isPending}
                        onClick={() => approve.mutate(r.id)}
                        className="rounded-full px-3 py-1 text-xs font-medium disabled:opacity-50"
                        style={{ background: "var(--amber)", color: "var(--ink)" }}
                      >
                        {t.translations_approve}
                      </button>
                      <button
                        disabled={reject.isPending}
                        onClick={() => {
                          const reason = window.prompt(t.translations_reject_prompt);
                          if (reason && reason.trim().length >= 3) {
                            reject.mutate({ id: r.id, reason: reason.trim() });
                          }
                        }}
                        className="rounded-full border px-3 py-1 text-xs font-medium disabled:opacity-50"
                        style={{ borderColor: "var(--rust)", color: "var(--rust)" }}
                      >
                        {t.translations_reject}
                      </button>
                    </div>
                  )}
                </div>
                <p className="mt-2 text-sm" style={{ color: "var(--ink)" }}>
                  {r.title ?? <em className="text-muted-foreground">untitled</em>}
                </p>
                <button
                  onClick={() => setOpen((o) => ({ ...o, [r.id]: !o[r.id] }))}
                  className="mt-2 text-[11px] uppercase tracking-wider text-muted-foreground hover:text-foreground"
                >
                  {isOpen ? t.translations_hide_body : t.translations_show_body}
                </button>
                {isOpen && (() => {
                  const d = drafts[r.id] ?? { title: r.title ?? "", body: r.body ?? "", reason: "" };
                  const dirty = d.title !== (r.title ?? "") || d.body !== (r.body ?? "");
                  return (
                    <div className="mt-3 grid gap-3 lg:grid-cols-2">
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                          {t.translation_original} · {original.language.toUpperCase()}
                        </p>
                        <p className="mt-1 text-sm font-medium" style={{ color: "var(--ink)" }}>
                          {original.title ?? <em className="text-muted-foreground">untitled</em>}
                        </p>
                        <pre
                          className="mt-2 max-h-[50vh] overflow-auto whitespace-pre-wrap rounded-sm border p-3 font-mono text-[12px] leading-relaxed"
                          style={{
                            borderColor: "var(--fog)",
                            background: "color-mix(in oklab, var(--paper) 96%, var(--ink) 4%)",
                          }}
                        >
                          {original.body ?? ""}
                        </pre>
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                          {t.translation_edited} · {r.language.toUpperCase()}
                        </p>
                        <input
                          value={d.title}
                          onChange={(e) => setDraft(r.id, { title: e.target.value, saved: false })}
                          className="mt-1 w-full rounded-sm border border-border bg-background px-2 py-1.5 text-sm"
                        />
                        <textarea
                          value={d.body}
                          onChange={(e) => setDraft(r.id, { body: e.target.value, saved: false })}
                          rows={16}
                          className="mt-2 w-full rounded-sm border border-border bg-background px-2 py-2 font-mono text-[12px] leading-relaxed"
                        />
                        <label className="mt-2 block text-[11px] text-muted-foreground">
                          {t.translation_reason_label}
                        </label>
                        <input
                          value={d.reason}
                          onChange={(e) => setDraft(r.id, { reason: e.target.value, saved: false })}
                          placeholder={t.translation_reason_placeholder}
                          className="mt-1 w-full rounded-sm border border-border bg-background px-2 py-1.5 text-xs"
                        />
                        <div className="mt-2 flex items-center gap-3">
                          <button
                            disabled={
                              save.isPending || !dirty || d.reason.trim().length < 3
                            }
                            onClick={() =>
                              save.mutate({
                                id: r.id,
                                title: d.title,
                                body: d.body,
                                reason: d.reason.trim(),
                              })
                            }
                            className="rounded-full border px-3 py-1 text-xs font-medium disabled:opacity-50"
                            style={{ borderColor: "var(--ink)", color: "var(--ink)" }}
                          >
                            {save.isPending ? "…" : t.save_translation}
                          </button>
                          {d.saved && !dirty && (
                            <span className="text-[11px] text-muted-foreground">
                              {t.translation_saved}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </li>
            );
          })}
        </ul>
      )}

      {rows.length > 0 && reviewIds.length === 0 && (
        <p className="mt-3 text-xs italic text-muted-foreground">
          {t.translations_none_review}
        </p>
      )}

      {notice && (
        <p className="mt-2 text-xs" style={{ color: "var(--rust)" }}>
          {notice}
          <button
            onClick={() => {
              setNotice(null);
              refetch();
            }}
            className="ml-2 underline"
          >
            retry
          </button>
        </p>
      )}
    </section>
  );
}